/**
 * The sixteen-agent roster, ported from `OEM+_Platform_Demo.html`.
 *
 * Source lines
 * ------------
 * - `AG`           — demo lines 2481–2497 (16 agents)
 * - `AG_FILL`      — demo lines 2498–2512 (14 "last run" computations)
 * - `agLast`       — demo line 2513 (the resolver)
 * - `renderAgents` — demo line 2514 (the consumer that names the fields)
 *
 * What changed in the port
 * ------------------------
 * 1. **The positional tuple became a named-field interface.** `renderAgents`
 *    indexes each agent array, which is where the field names come from:
 *
 *    | index | field         | demo usage                                        |
 *    |-------|---------------|---------------------------------------------------|
 *    | `[0]` | `name`        | card `<h3>`, DOM id, `AGENT_COL` key, `MODULES[].agents` key |
 *    | `[1]` | `description` | the `<p>` under the heading                       |
 *    | `[2]` | `reads`       | `<dt>Reads</dt>` — the inputs                     |
 *    | `[3]` | `produces`    | `<dt>Produces</dt>` — the outputs                 |
 *    | `[4]` | `level`       | autonomy pill; `L2` prints "Automated", `L1` "Assisted" |
 *    | `[5]` | `lastRun`     | either a literal string or an `AG_FILL` token     |
 *    | `[6]` | `module`      | module id, used for the "NN Module ›" footer link |
 *
 * 2. **`AG_FILL` was not data — it was fourteen zero-argument closures over the
 *    loaded datasets** (`ALERTS`, `WOS`, `B.cases`, `B.orders`, `B.tapp_jobs`,
 *    `B.mx`, `HEALTH`, `FLEET_RULES`, `EXCHANGE`, `predictedUsage()`,
 *    `promises()`, `upgradeCandidates()`, `renewals()`…). Porting them as data
 *    would mean baking this morning's numbers into the seed, so instead **each
 *    one is now a documented pure function** over an explicit
 *    {@link AgentLastRunContext} — see {@link AGENT_LAST_RUN}. The functions
 *    import nothing, read no globals and have no side effects; the caller
 *    supplies every number. Two of the fourteen (`__CAP__`, `__WQ__`) were
 *    already constant strings in the demo and take no context at all.
 *
 *    The demo's `agLast` wrapped each call in a `try/catch` that fell back to
 *    printing the raw token. That was a load-order guard against a helper not
 *    yet being defined; with an explicit context there is nothing to throw, so
 *    {@link agentLastRunText} has no catch. The one genuine conditional inside
 *    `__PROMISE__` (`typeof promises==='function' ? … : 'promises issued on
 *    every open alert'`) *is* reproduced, as a nullable `promises` field.
 *
 * 3. **No handler strings.** The footer button's inline handler, a
 *    `show('module','<id>')` call, is rebuilt by the scene from the typed
 *    `module` field; the card-level `show('agents','<name>')` handler used by
 *    `renderModule` likewise belongs to the scene.
 *
 * 4. **Machine ids stay symbolic.** `__DIAG__`, `__COPILOT__` and `__SAFETY__`
 *    interpolated `IDS.T22` / `IDS.L14`, i.e. ids looked up from the data pack.
 *    They are resolved from the caller's {@link MachineIdMap}, never hard-coded.
 *
 * 5. **Swatches live in `modules.ts`.** The demo's `AGENT_COL` is module-palette
 *    metadata, so it is exported from `modules.ts` as `AGENT_COLOURS`.
 *
 * Data Schema layer — pure data and pure functions. No React, no side effects,
 * no imports from `modules/`.
 */

import type { MachineIdMap, ModuleId } from './modules';

/* ------------------------------------------------------------------ *
 * Roster
 * ------------------------------------------------------------------ */

/** Autonomy level. `L2` runs automated, `L1` is assisted. */
export type AgentLevel = 'L1' | 'L2';

/** The `AG_FILL` keys, demo lines 2498–2512. */
export const AGENT_FILL_TOKENS = [
  '__ORCH__',
  '__DIAG__',
  '__PARTS__',
  '__COPILOT__',
  '__SAFETY__',
  '__LEARN__',
  '__IB__',
  '__DEMAND__',
  '__INV__',
  '__CAP__',
  '__PROMISE__',
  '__COMP__',
  '__WQ__',
  '__COMM__',
] as const;

/** Closed set of `AG_FILL` keys. */
export type AgentFillToken = (typeof AGENT_FILL_TOKENS)[number];

/**
 * What the demo printed in the agent card's "Last run" row.
 *
 * Two agents — Knowledge and Work Order — carried a fixed sentence in `AG[5]`
 * with no matching `AG_FILL` entry; `agLast` printed it unchanged.
 */
export type AgentLastRun =
  | { readonly kind: 'literal'; readonly text: string }
  | { readonly kind: 'computed'; readonly token: AgentFillToken };

/** One agent on the roster. */
export interface OemAgent {
  /** Agent name — the key `OemModule.agents` and `AGENT_COLOURS` join on. */
  readonly name: string;
  /** What the agent does. */
  readonly description: string;
  /** What it reads. */
  readonly reads: string;
  /** What it produces. */
  readonly produces: string;
  /** Autonomy level. */
  readonly level: AgentLevel;
  /** The "Last run" row. */
  readonly lastRun: AgentLastRun;
  /** The module this agent runs in. */
  readonly module: ModuleId;
}

/** Demo lines 2481–2497 — `const AG=[…]`, 16 agents in demo order. */
export const AGENTS: readonly OemAgent[] = [
  {
    name: 'Orchestrator',
    description:
      'Sequences the specialists per event, case or incident, resolves conflicting recommendations, tracks state end to end and escalates the moment a guardrail is reached.',
    reads: 'Alerts, cases, agent outputs, approvals',
    produces: 'Case state, routing, escalations, audit log',
    level: 'L2',
    lastRun: { kind: 'computed', token: '__ORCH__' },
    module: 'orchestrate',
  },
  {
    name: 'Diagnostic',
    description:
      'Fuses live and shift-end telemetry, alarms, camera events, oil results and the machine\'s own history into a ranked failure mode with a time-to-failure window and a confidence.',
    reads: 'RMS · Pitram · MQTT · vision · oil lab · CMMS history',
    produces: 'Predicted failure, window, confidence, evidence',
    level: 'L2',
    lastRun: { kind: 'computed', token: '__DIAG__' },
    module: 'diagnose',
  },
  {
    name: 'Knowledge',
    description:
      'Answers in plain language grounded in the technical library, the parts books and the bulletins, with a citation on every claim.',
    reads: 'SharePoint · CatBase · bulletins · transcripts',
    produces: 'Procedures, torques, warnings, sources',
    level: 'L2',
    lastRun: { kind: 'literal', text: 'Attached SP-MJ2D-RD-011 + SP-MT63-BRK-002' },
    module: 'diagnose',
  },
  {
    name: 'Parts & Procurement',
    description:
      'Resolves the part from a drawing callout, prices it, checks the site store and every DC, chooses road or air against the failure window and drafts the requisition.',
    reads: 'CatBase · Salesforce · inventory · predicted failures',
    produces: 'Quotes, draft requisitions, freight options, reservations',
    level: 'L2',
    lastRun: { kind: 'computed', token: '__PARTS__' },
    module: 'parts',
  },
  {
    name: 'Work Order',
    description:
      'Drafts and files work orders and close-out reports in the service register and the customers\' systems; keeps the component register and asset history in sync.',
    reads: 'Maximo · SAP PM · agent outputs',
    produces: 'Draft WOs, close-out chronicles, register updates',
    level: 'L2',
    lastRun: { kind: 'literal', text: 'WO-26-11913, WO-26-11914, WO-26-11917 drafted' },
    module: 'closeout',
  },
  {
    name: 'Technician Copilot',
    description:
      'Hands-free steps, torques and safety warnings on the headset or the app; records failure mode, photos and readings mid-job.',
    reads: 'Procedures · training transcripts · live telemetry',
    produces: 'Guided steps, captured evidence, labour lines',
    level: 'L2',
    lastRun: { kind: 'computed', token: '__COPILOT__' },
    module: 'execute',
  },
  {
    name: 'Safety & Compliance',
    description:
      'Checks every proposed action against isolation standards, bulletins, the seismic protocol and site permits before it reaches a technician — and can veto or hold any of it.',
    reads: 'Safety system · bulletins · MI-SEIS-002 · WO drafts',
    produces: 'Clearance, added controls, holds, restrictions',
    level: 'L2',
    lastRun: { kind: 'computed', token: '__SAFETY__' },
    module: 'execute',
  },
  {
    name: 'Fleet Learning',
    description:
      'Mines every closed work order and incident for the rule the whole fleet should run on; attributes cost to machine, crew, practice or ground conditions; tracks diesel, kWh and CO₂-e.',
    reads: 'Closed WOs · ledger · shift energy · outcomes',
    produces: 'Fleet rules, interval proposals, cost & emissions attribution',
    level: 'L1',
    lastRun: { kind: 'computed', token: '__LEARN__' },
    module: 'closeout',
  },
  {
    name: 'Installed Base',
    description:
      'Keeps one record per serial number current — configuration, meters, location, components, contract and documents — across the OEM register and the customers\' own systems.',
    reads: 'Maximo service register · customer EAM · RMS · shift-end',
    produces: 'Asset records, meters, cross-references, hot spots',
    level: 'L2',
    lastRun: { kind: 'computed', token: '__IB__' },
    module: 'installed',
  },
  {
    name: 'Demand Planning',
    description:
      'Senses where the next job comes from — the PM plan, condition, prediction, reactive failures, wear, lifecycle events and the commercial calendar — and forecasts the demand that follows.',
    reads: 'Condition register · PM schedule · alerts · contracts',
    produces: 'Demand forecast, S.O.S daily report, planning horizon',
    level: 'L2',
    lastRun: { kind: 'computed', token: '__DEMAND__' },
    module: 'demand',
  },
  {
    name: 'Inventory Network',
    description:
      'Plans stock across the network rather than the store: multi-echelon minimums from predicted usage, transfers between DCs and the stock positions that hold a promise together.',
    reads: 'Inventory · predicted usage · lead times · freight',
    produces: 'Minimum-level proposals, transfers, positioning plan',
    level: 'L2',
    lastRun: { kind: 'computed', token: '__INV__' },
    module: 'parts',
  },
  {
    name: 'Service Capacity',
    description:
      'Matches jobs to people by roster, skills, certificates, location and customer preference; rates each job from measured outcomes; balances capacity against sensed demand.',
    reads: 'Technician app · roster · LMS · CSS · GPS',
    produces: 'Dispatch proposals, utilisation, skills gaps, technician rating',
    level: 'L2',
    lastRun: { kind: 'computed', token: '__CAP__' },
    module: 'capacity',
  },
  {
    name: 'Service Promise',
    description:
      'Turns a plan into a commitment: parts, technician, tools and the customer\'s own window resolved into an executable date, issued as a confirmed time window and kept updated in the customer\'s channel.',
    reads: 'Parts position · roster · customer window · job state',
    produces: 'Executable date, service promise, customer notifications',
    level: 'L2',
    lastRun: { kind: 'computed', token: '__PROMISE__' },
    module: 'promise',
  },
  {
    name: 'Component Lifecycle',
    description:
      'Tracks serialised components from install to removal, predicts the change-out, manages the exchange pool and the core returns, and carries each serial\'s history into its next life.',
    reads: 'Maximo sub-assets · condition register · exchange pool',
    produces: 'Removal forecasts, exchange allocation, core routing',
    level: 'L1',
    lastRun: { kind: 'computed', token: '__COMP__' },
    module: 'closeout',
  },
  {
    name: 'Warranty & Quality',
    description:
      'Processes claims against the evidence on the record, clusters failures across the fleet, and turns the root cause into a bulletin or a campaign with its parts and labour planned.',
    reads: 'Cases · failure reports · incidents · bulletins',
    produces: 'Claim positions, failure clusters, bulletins, campaign plans',
    level: 'L2',
    lastRun: { kind: 'computed', token: '__WQ__' },
    module: 'commercial',
  },
  {
    name: 'Commercial',
    description:
      'Settles who pays before a draft becomes a promise — warranty, contract, campaign or goodwill — checks the approval limit, and reads the installed base for upgrades, renewals and additional work.',
    reads: 'Contracts · warranty · rate card · CSS · installed base',
    produces: 'Entitlement, approval routing, upgrade and renewal proposals',
    level: 'L2',
    lastRun: { kind: 'computed', token: '__COMM__' },
    module: 'commercial',
  },
];

/** `AGENTS` keyed by name. */
export const AGENT_BY_NAME: Readonly<Record<string, OemAgent>> = Object.fromEntries(
  AGENTS.map((a) => [a.name, a]),
);

/* ------------------------------------------------------------------ *
 * "Last run" — AG_FILL as pure functions
 * ------------------------------------------------------------------ */

/**
 * Promise counts, or `null`.
 *
 * The demo guarded `__PROMISE__` with `typeof promises==='function'` because
 * the `promises()` helper is defined further down the file than the roster.
 * `null` reproduces that miss and yields the demo's fallback sentence.
 */
export interface PromiseTally {
  /** `promises().filter(p=>p.inside).length` */
  readonly inside: number;
  /** `promises().length` */
  readonly total: number;
}

/**
 * Everything the fourteen "last run" sentences need, named per demo expression.
 *
 * Supplied by the caller. Nothing in this module reaches for a global, so the
 * functions below are referentially transparent and trivially testable.
 */
export interface AgentLastRunContext {
  /** `ALERTS.length` */
  readonly openAlerts: number;
  /** `B.cases.filter(c=>/Open/.test(c.status)).length` */
  readonly openCases: number;
  /** `WOS.filter(w=>w.status.startsWith('Draft')).length` */
  readonly draftWorkOrders: number;
  /** `B.orders.filter(o=>/agent/i.test(o.status)).length` */
  readonly agentRequisitions: number;
  /** `B.tapp_jobs.filter(j=>j.status==='In progress').length` */
  readonly jobsInProgress: number;
  /** `FLEET_RULES.length` */
  readonly fleetRules: number;
  /** `Object.keys(B.mx).length` */
  readonly assetRecords: number;
  /** `Object.values(B.mx).reduce((a,m)=>a+(m.children||[]).length,0)` */
  readonly serialisedSubAssets: number;
  /** `HEALTH.filter(h=>ATTN.includes(h.band)).length` */
  readonly flaggedComponents: number;
  /** `WOS.filter(w=>w.wo_type==='Preventive'&&w.status==='Scheduled'&&w.created>'2026-09-09').length` */
  readonly pmsScheduledAhead: number;
  /** `predictedUsage().length` */
  readonly minimumLevelProposals: number;
  /** `promises()`, or `null` when the helper is not available. */
  readonly promises: PromiseTally | null;
  /** `EXCHANGE.length` */
  readonly exchangeUnits: number;
  /** `B.mx_contracts.length` */
  readonly contracts: number;
  /** `upgradeCandidates().length` */
  readonly upgradeCandidates: number;
  /** `renewals()[0].days` */
  readonly nextRenewalDays: number;
  /** The demo's `IDS` map — machine ids are never hard-coded. */
  readonly machineIds: MachineIdMap;
}

/**
 * The demo's `AG_FILL`, rewritten as pure functions of an explicit context.
 *
 * Every literal below — spacing, `·` separators, casing — is copied verbatim
 * from demo lines 2498–2512. `__CAP__` and `__WQ__` were already constant in
 * the demo and therefore ignore the context.
 */
export const AGENT_LAST_RUN: Readonly<
  Record<AgentFillToken, (ctx: AgentLastRunContext) => string>
> = {
  /** Demo line 2498. */
  __ORCH__: (ctx) =>
    `${ctx.openAlerts} open alerts · ${ctx.openCases} open cases · ${ctx.draftWorkOrders} drafts awaiting a person`,
  /** Demo line 2499. */
  __DIAG__: (ctx) =>
    `${ctx.machineIds.T22} brakes 0.91 · L-33 charge connector 0.74 · S-09 rotary head 0.81`,
  /** Demo line 2500. */
  __PARTS__: (ctx) =>
    `${ctx.agentRequisitions} requisitions drafted · SO-90435 recommended by hand-carry rather than Bass Strait freight`,
  /** Demo line 2501. */
  __COPILOT__: (ctx) =>
    `${ctx.jobsInProgress} jobs in progress · 66 min headset transcript on the ${ctx.machineIds.L14} coupling job`,
  /** Demo line 2502. */
  __SAFETY__: (ctx) =>
    `${ctx.machineIds.T22} empty-haul restriction drafted · bay 2 locked out at Mount Read (SB-2026-005)`,
  /** Demo line 2503. */
  __LEARN__: (ctx) =>
    `${ctx.fleetRules} fleet rules now running · Kalbarri crew flag · Gawler Deep hose campaign`,
  /** Demo line 2504. */
  __IB__: (ctx) =>
    `${ctx.assetRecords} asset records · ${ctx.serialisedSubAssets} serialised sub-assets · Mount Read onboarding at 35%`,
  /** Demo line 2505. */
  __DEMAND__: (ctx) =>
    `${ctx.flaggedComponents} flagged components · ${ctx.pmsScheduledAhead} PMs scheduled ahead`,
  /** Demo line 2506. */
  __INV__: (ctx) =>
    `${ctx.minimumLevelProposals} minimum-level proposals · Newcastle DC front-end kits raised`,
  /** Demo line 2507 — constant in the demo too. */
  __CAP__: () => 'Kieran Walsh only DTH-certified tech in NT · Lucas Ferreira HV expiring 18 d',
  /** Demo line 2508. */
  __PROMISE__: (ctx) =>
    ctx.promises === null
      ? 'promises issued on every open alert'
      : `${ctx.promises.inside} of ${ctx.promises.total} promises inside the failure window`,
  /** Demo line 2509. */
  __COMP__: (ctx) => `${ctx.exchangeUnits} exchange units in play · 2 cores in the rebuild queue`,
  /** Demo line 2510 — constant in the demo too. */
  __WQ__: () =>
    'CS-2026-2210 declined on the oil evidence · TB-2026-034 issued · ML-21 hose campaign 1 of 4',
  /** Demo line 2511. */
  __COMM__: (ctx) =>
    `${ctx.contracts} contracts read · ${ctx.upgradeCandidates} upgrade candidates · Broken Ridge renews in ${ctx.nextRenewalDays} days`,
};

/**
 * The demo's `agLast` (line 2513), as a pure function.
 *
 * A literal last-run string is returned unchanged; a computed one is resolved
 * through {@link AGENT_LAST_RUN} against the supplied context.
 */
export const agentLastRunText = (agent: OemAgent, ctx: AgentLastRunContext): string =>
  agent.lastRun.kind === 'literal'
    ? agent.lastRun.text
    : AGENT_LAST_RUN[agent.lastRun.token](ctx);
